import {
  type FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import * as cardValidator from 'card-validator';
import classNames from 'classnames';
import {action, reaction} from 'mobx';
import {Observer} from 'mobx-react';

import {useI18n} from '@yourcoach/i18n';
import {cardStore} from '@yourcoach/shared/api/payment';
import Lines from '@yourcoach/shared/assets/icons/lines-card.svg';
import {alert} from '@yourcoach/shared/components/alert';
import {Image} from '@yourcoach/shared/uikit/Image';
import {handle3dSecure} from '@yourcoach/shared/utils/entity/card';
import {getCardBrandImage} from '@yourcoach/shared/utils/entity/card/getCardBrandImage';
import {applyMask} from '@yourcoach/shared/utils/format';
import {logger} from '@yourcoach/shared/utils/logger';
import {validateFields} from '@yourcoach/shared/utils/validation';
import {createHtmlInputField} from '@yourcoach/shared/utils/validation/createHtmlInputField';

import Button from '@src/components/Button';
import Loader from '@src/components/Loader/Loader';
import TextField from '@src/components/TextField';
import useIsVisible from '@src/hooks/useIsVisible';

import styles from './AddCard.module.css';

const DEFAULT_CARD = {
  cvv: {
    mask: '...',
    placeholder: 'CVV',
  },
  number: {
    mask: '.... .... .... ....',
  },
};

export interface Props {}

const AddCard: FC<Props> = () => {
  const {t} = useI18n();

  useEffect(() => {
    logger.event('payment_method_add_card_view');
  }, []);

  const expDateRef = useRef('');
  const [expDateError, setExpDateError] = useState('');
  const [brand, setBrand] = useState('');

  const brandImageSrc = useMemo(() => getCardBrandImage(brand, true), [brand]);

  const [overlayIsVisible, showOverlay, hideOverlay] = useIsVisible();

  const fields = useRef({
    cvv: createHtmlInputField('cvv', {
      customValidate: action(() => {
        const field = fields.cvv;

        field.value = applyMask(
          (field.value.match(/\d/g) || []).join(''),
          field.extraFields!.mask,
        );

        const validation = cardValidator.cvv(
          field.value,
          field.extraFields!.mask.length,
        );

        if (!field.value) {
          field.error = t(
            'web-app:AddCard.security_code_required_error_message',
          );
          field.isValid = false;

          return false;
        }

        const isValid = validation.isPotentiallyValid || validation.isValid;

        field.error = !isValid
          ? t('web-app:AddCard.security_code_not_valid_error_message')
          : '';

        return isValid;
      }),
      extraFields: {
        mask: '...',
        placeholder: 'CVV',
      },
    }),
    expMonth: createHtmlInputField('expMonth', {
      customValidate: action(() => {
        const field = fields.expMonth;

        field.value = applyMask(
          (field.value.match(/\d/g) || []).join(''),
          field.extraFields!.mask,
        );

        return true;
      }),
      extraFields: {
        mask: '..',
      },
    }),
    expYear: createHtmlInputField('expYear', {
      customValidate: action(() => {
        const field = fields.expYear;

        field.value = applyMask(
          (field.value.match(/\d/g) || []).join(''),
          field.extraFields!.mask,
        );

        return true;
      }),
      extraFields: {
        mask: '..',
      },
    }),
    number: createHtmlInputField('number', {
      customValidate: action(() => {
        const field = fields.number;

        field.value = applyMask(
          (field.value.match(/\d/g) || []).join(''),
          field.extraFields!.mask,
        );

        const validation = cardValidator.number(field.value);

        if (validation.card) {
          setBrand(validation.card.type);

          const mask = Array(Math.max(...validation.card.lengths)).fill('.');

          validation.card.gaps.forEach((gapIndex, i) => {
            mask.splice(gapIndex + i, 0, ' ');
          });

          field.extraFields!.mask = mask.join('');

          fields.cvv.extraFields!.placeholder = validation.card.code.name;
          fields.cvv.extraFields!.mask = Array(validation.card.code.size)
            .fill('.')
            .join('');
        } else {
          setBrand('');

          field.extraFields!.mask = DEFAULT_CARD.number.mask;

          fields.cvv.extraFields!.placeholder = DEFAULT_CARD.cvv.placeholder;
          fields.cvv.extraFields!.mask = DEFAULT_CARD.cvv.mask;
        }

        if (!field.value) {
          field.error = t('web-app:AddCard.card_number_required_error_message');
          field.isValid = false;

          return false;
        }

        const isValid = validation.isPotentiallyValid || validation.isValid;

        field.error = !isValid
          ? t('web-app:AddCard.card_number_not_valid_error_message')
          : '';
        field.isValid = isValid;

        return isValid;
      }),
      extraFields: {
        mask: '.... .... .... ....',
      },
    }),
  }).current;

  useEffect(() => {
    if (fields.number.ref && fields.number.ref.current) {
      fields.number.ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateExpDate = useCallback(
    (expDate: string) => {
      const validation = cardValidator.expirationDate(expDate);

      if (!expDate || expDate === '/') {
        setExpDateError(t('web-app:AddCard.exp_date_required_error_message'));

        return false;
      }

      const isValid = validation.isPotentiallyValid || validation.isValid;

      setExpDateError(
        !isValid ? t('web-app:AddCard.exp_date_not_valid_error_message') : '',
      );

      return isValid;
    },
    [t],
  );

  useEffect(() => {
    const dispose = reaction(
      () => `${fields.expMonth.value}/${fields.expYear.value}`,
      expDate => {
        expDateRef.current = expDate;

        validateExpDate(expDate);
      },
    );

    return () => {
      dispose();
    };
  }, [
    fields.expMonth.value,
    fields.expYear.value,
    fields.number,
    validateExpDate,
  ]);

  const validateCard = useCallback(() => {
    const fieldsAreValid = validateFields(fields);
    const expDateIsValid = validateExpDate(expDateRef.current);

    return fieldsAreValid && expDateIsValid;
  }, [fields, validateExpDate]);

  const createCard = useCallback(async () => {
    const cardData = {
      cvc: fields.cvv.value,
      exp_month: fields.expMonth.value,
      exp_year: fields.expYear.value,
      number: fields.number.value,
    };

    try {
      showOverlay();

      await cardStore.addCard({
        data: cardData,
        handle3dSecure,
        returnUrl: window.location.href,
      });

      hideOverlay();
    } catch (error) {
      hideOverlay();

      logger.error(error);

      alert.error(error);
    }
  }, [
    fields.cvv.value,
    fields.expMonth.value,
    fields.expYear.value,
    fields.number.value,
    hideOverlay,
    showOverlay,
  ]);

  const onAddCardButtonClick = useCallback(() => {
    logger.event('payment_method_add_card_tap');

    if (validateCard()) {
      createCard();
    }
  }, [createCard, validateCard]);

  return (
    <div className={classNames('AddCard', styles.Component)}>
      <div className={styles.cardsContainersWrapper}>
        <div className={styles.cardsContainers}>
          <div className={styles.cardFront}>
            <div className={styles.lines}>
              <Lines />
            </div>
            <div className={styles.bankLogoContainer} />
            <Observer>
              {() => (
                <div className={styles.inputContainer}>
                  {fields.number.error ? (
                    <div className={styles.errorInputBorder} />
                  ) : null}
                  <TextField
                    maxLength={
                      fields.number.extraFields!.mask.length || undefined
                    }
                    onChange={fields.number.onChange}
                    placeholder={t('web-app:AddCard.card_number_label')}
                    ref={fields.number.ref}
                    value={fields.number.value}
                  />
                  {fields.number.error ? (
                    <div className={styles.numberErrorContainer}>
                      <span className={styles.errorText}>
                        {fields.number.error}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            </Observer>
            <div className={styles.expirationDateLabel}>
              {t('web-app:AddCard.exp_date_label')}
            </div>
            <div className={styles.expirationDateContainer}>
              <div className={styles.inputContainer}>
                {expDateError ? (
                  <div className={styles.errorInputBorder} />
                ) : null}
                <Observer>
                  {() => (
                    <TextField
                      className={styles.expirationDateInput}
                      maxLength={2}
                      onChange={fields.expMonth.onChange}
                      placeholder="MM"
                      ref={fields.expMonth.ref}
                      value={fields.expMonth.value}
                    />
                  )}
                </Observer>
              </div>
              <div className={styles.expirationDateSeparator} />
              <div className={styles.inputContainer}>
                {expDateError ? (
                  <div className={styles.errorInputBorder} />
                ) : null}
                <Observer>
                  {() => (
                    <TextField
                      className={styles.expirationDateInput}
                      maxLength={2}
                      onChange={fields.expYear.onChange}
                      placeholder="YY"
                      ref={fields.expYear.ref}
                      value={fields.expYear.value}
                    />
                  )}
                </Observer>
              </div>
              {brandImageSrc ? (
                <Image
                  className={styles.cardBrand}
                  resizeMode="contain"
                  src={brandImageSrc}
                />
              ) : null}
              {expDateError ? (
                <div className={styles.expDateErrorContainer}>
                  <span className={styles.errorText}>{expDateError}</span>
                </div>
              ) : null}
            </div>
          </div>
          <div className={styles.cardBack}>
            <div className={styles.line} />
            <div className={styles.securityCodeWrapper}>
              <div className={styles.securityCodeContainer}>
                <Observer>
                  {() => (
                    <>
                      <div className={styles.inputContainer}>
                        {fields.cvv.error ? (
                          <div className={styles.errorInputBorder} />
                        ) : null}
                        <TextField
                          maxLength={fields.cvv.extraFields!.mask.length}
                          onChange={fields.cvv.onChange}
                          placeholder={fields.cvv.extraFields!.placeholder}
                          ref={fields.cvv.ref}
                          type="password"
                          value={fields.cvv.value}
                        />
                      </div>
                      <div
                        className={classNames(
                          styles.securityCodeLabel,
                          fields.cvv.error && styles.cvvError,
                        )}
                      >
                        {fields.cvv.error
                          ? fields.cvv.error
                          : t('web-app:AddCard.security_code_label')}
                      </div>
                    </>
                  )}
                </Observer>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.actionButtonsContainer}>
        <Button onClick={onAddCardButtonClick}>{t('shared:add')}</Button>
      </div>
      {overlayIsVisible ? (
        <div className="overlay">
          <Loader />
        </div>
      ) : null}
    </div>
  );
};

export default memo(AddCard);
